import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  IconButton,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ViewInAr as ModelIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const ModelManager = ({ brandId, brandSlug }) => {
  const [loading, setLoading] = useState(true);
  const [models, setModels] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  const getAuthHeaders = (isJson = true) => {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (isJson) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  };

  useEffect(() => {
    fetchModels();
  }, [brandId]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/storefront/models?brandId=${brandId}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      setModels(data.models || []);
    } catch (error) {
      toast.error('Failed to load models');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(`Uploading files...`);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress(`Uploading ${file.name} (${i + 1}/${files.length})...`);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('brandId', brandId);

        const response = await fetch('/api/storefront/models/upload', {
          method: 'POST',
          headers: getAuthHeaders(false),
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }
      }

      setUploadProgress('Upload complete!');
      toast.success(`Uploaded ${files.length} model(s)`);
      setTimeout(() => {
        setUploadProgress('');
        setUploading(false);
        fetchModels();
      }, 1500);
    } catch (err) {
      toast.error('Upload failed: ' + err.message);
      setUploading(false);
      setUploadProgress('');
    }

    event.target.value = '';
  };

  const handleDelete = async (modelPath) => {
    if (!window.confirm(`Delete ${modelPath}?`)) return;

    try {
      const response = await fetch('/api/storefront/models/delete', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ brandId, path: modelPath }),
      });

      if (response.ok) {
        toast.success('Model deleted');
        fetchModels();
      } else {
        toast.error('Failed to delete model');
      }
    } catch (err) {
      toast.error('Error deleting model: ' + err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading models...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          3D Model Library ({models.length})
        </Typography>

        <Button
          variant="contained"
          component="label"
          startIcon={<UploadIcon />}
          disabled={uploading}
        >
          {uploading ? uploadProgress : 'Upload GLB Files'}
          <input
            type="file"
            hidden
            accept=".glb,.gltf"
            multiple
            onChange={handleFileUpload}
            disabled={uploading}
          />
        </Button>
      </Box>

      {uploading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {uploadProgress}
          </Typography>
        </Box>
      )}

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Model Directory:</strong> /uploads/brands/{brandSlug}/models/<br />
        <strong>Supported formats:</strong> .glb, .gltf<br />
        <strong>Tip:</strong> Use descriptive names like "black-angel-hoodie.glb"
      </Alert>

      {/* Models Grid */}
      {models.length === 0 ? (
        <Alert severity="info">
          No 3D models found. Upload some GLB files to get started!
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {models.map((model) => {
            const fileName = model.path ? model.path.replace(/.*\//, '') : model;
            const modelPath = model.path || model;
            const isPlaceholder = fileName === 'placeholder.glb';

            return (
              <Grid item xs={12} sm={6} md={4} key={modelPath}>
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: isPlaceholder ? 'warning.main' : 'divider',
                    bgcolor: isPlaceholder ? 'warning.lighter' : 'background.paper',
                    borderWidth: 2
                  }}
                >
                  <CardContent>
                    {/* Model Icon Preview */}
                    <Box
                      sx={{
                        width: '100%',
                        height: 120,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      <ModelIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    </Box>

                    {/* File Name */}
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {fileName}
                    </Typography>

                    {isPlaceholder && (
                      <Chip
                        label="Default Placeholder"
                        color="warning"
                        size="small"
                        sx={{ mt: 1, mb: 1 }}
                      />
                    )}

                    {/* Path */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      {modelPath}
                    </Typography>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<ViewIcon />}
                        component="a"
                        href={modelPath}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </Button>
                      {!isPlaceholder && (
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(modelPath)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default ModelManager;
